import { dehydrate } from "@tanstack/react-query";
import { useParams, usePathname } from "next/navigation";
import { GetStaticPropsContext } from "next/types";
import { NextSeo, BreadcrumbJsonLd } from "next-seo";
import { Breadcrumb } from "@/components/Breadcrumb";
import ContainerBoundary from "@/components/common/ContainerBoundary";
import PlayerNewsContainer from "@/components/container/PlayerNewsContainer";
import { PLAYER_NEWS_COMPETITION_PAGE_VIEWED } from "@/constants/amplitude";
import { PlayerNewsMeta } from "@/constants/playerNews";
import { BreadcrumbSegment } from "@/types/breadcrumbs";
import { FilterOptions } from "@/types/playerNews";
import {
  getAvailableSportsPN,
  getContentFiltersApi,
  getSupportedSportsFiltersApi,
} from "@/utils/api";
import { useAmplitude } from "@/utils/hooks/useAmplitude";
import { useBreadcrumbs } from "@/utils/hooks/useBreadcrumbs";
import useOnce from "@/utils/hooks/useOnce";
import logger from "@/utils/logger";
import {
  findSport,
  getSeoMetaBySlug,
  extractFilters,
} from "@/utils/playerNews";
import { loadHeader, loadPlayerNews } from "@/utils/ssr";

interface PlayerNewsPageProps {
  sportName: string;
  filterOptions: FilterOptions;
  seo: PlayerNewsMeta;
}

export default function PlayerNewsPage({
  sportName,
  filterOptions,
  seo,
}: Readonly<PlayerNewsPageProps>) {
  const { logAmplitudeEvent } = useAmplitude();
  const pathname = usePathname();
  const params = useParams();

  useOnce(() => {
    logAmplitudeEvent(PLAYER_NEWS_COMPETITION_PAGE_VIEWED, {
      Competition: sportName,
    });
  });

  const slug = params?.slug as string;
  const breadcrumbSegments: BreadcrumbSegment[] = [
    { name: sportName.toUpperCase(), path: `/${slug}` },
    { name: "Player News", path: `/${slug}/player-news` },
    { name: seo.title, path: pathname },
  ];
  const { trail, itemListElements } = useBreadcrumbs(breadcrumbSegments);

  return (
    <>
      <NextSeo title={seo.metaTitle} description={seo.metaDescription} />
      <BreadcrumbJsonLd itemListElements={itemListElements} />
      <ContainerBoundary legacy>
        <Breadcrumb trail={trail} />
      </ContainerBoundary>
      <PlayerNewsContainer
        title={seo.title}
        sportName={sportName}
        sportFilters={filterOptions}
        newsType={null}
      />
    </>
  );
}

export async function getServerSideProps({ params }: GetStaticPropsContext) {
  const [
    availableSportsResponse,
    getContentFiltersApiResponse,
    getSupportedSportsFiltersApiResponse,
  ] = await Promise.all([
    getAvailableSportsPN(),
    getContentFiltersApi(),
    getSupportedSportsFiltersApi(),
  ]);

  const slug = params?.slug?.toString() ?? "";
  const selectedSport = findSport(availableSportsResponse, slug);
  const filterOptions = extractFilters(
    getSupportedSportsFiltersApiResponse,
    getContentFiltersApiResponse,
    slug
  );

  if (!selectedSport) return { notFound: true };

  try {
    const menuClient = await loadHeader();
    const playerNewsClient = selectedSport.contentSport
      ? await loadPlayerNews(menuClient, selectedSport.contentSport)
      : menuClient;
    const seo = getSeoMetaBySlug(slug, selectedSport.name);

    return {
      props: {
        dehydratedState: dehydrate(playerNewsClient),
        sportName: selectedSport.name,
        seo,
        filterOptions,
      },
    };
  } catch (error) {
    logger.error("PlayerNews root page error", error);
    return { notFound: true };
  }
}
